import React from "react";
import { StyleSheet, TouchableOpacity } from "react-native";
import { useSelector } from "react-redux";
import { useSlateStatic } from "slate-react";
import lodashTemplate from "lodash/template";

import * as connectionDb from "src/db/connection.db";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";

import { PdPaper } from "src/components/Icons";
import { PdText } from "src/components/layout";
import { PdPopover, PdPopoverContent, PdPopoverTrigger } from "src/components/overlay";

import { ComposerToolbarButton, ComposerToolbarButtonLabel } from "./components";
import { insertDataHtml, useComposer } from "./utils";
import { kBaseSiteUrl } from "src/util/constants";

type tTemplate = { value: string; label: string };

const templateBrandOptions: tTemplate[] = [
  {
    label: "Welcome Template (New Retailer)",
    value: `<p>Hi <%- to %>,<br><br>We're excited to connect with you and to introduce you to our brand. Whether you're already familiar with our products or you've just discovered us, it would be our pleasure to answer any questions you might have and help guide you in the selection process of your first order with us.<br><br>We look forward to working with you!<br><br><%- from %></p>`,
  },
  {
    label: "Welcome Template (Existing Retailer)",
    value: `<p>Hi <%- to %>,<br><br>We're thrilled to connect with you on The Main Tab! We encourage you to visit our brand page and shop your favorite products and discover new releases. You can be sure to rely on our quick order process and shipment and we hope that you'll take advantage of the generous Net 60 Terms available via The Main Tab.<br><br>We'd love to hear from you with any questions or feedback about our products!<br><br>Thank you for your ongoing support,<br><br><%- from %></p>`,
  },
  {
    label: "Draft Order Template",
    value: `<p>Hi <%- to %>,<br><br>It's great to connect with you on The Main Tab. We understand and appreciate a busy schedule so we'd love to suggest a potential order with some of our best-sellers! This is an interactive order which you can edit as it best suits your business needs.<br><br>I'd love to hear your thoughts and feedback on the selection of our products.<br><br><b>(INSERT LINK TO ORDER HERE)</b>.<br><br>Thank you for your ongoing support,<br><%- from %></p>`,
  },
  {
    label: "New Launches Template",
    value: `<p>Hi <%- to %>,<br><br>We're excited to share with you the newest addition(s) to our product line. We encourage you to visit our brand page and sort by “Recently Added” for a full view. I took the liberty to create a suggested order of the new item(s) for your review and consideration.<br><br><b>(INSERT LINK TO ORDER HERE)</b><br><br>I look forward to your thoughts and feedback on our latest launch.<br><br>Thank you for your ongoing support,<br><%- from %></p>`,
  },
  {
    label: "Checking In Template",
    value: `<p>Hi <%- to %>,<br><br>I hope you are well. Taking a moment to check in with you to see how business is going and how our brand is performing in your store. Any feedback or insight that you can share with me, is valuable and truly appreciated.<br><br>Thank you for your continued support!<br><br>Warmly,<br><%- from %></p>`,
  },
  {
    label: "Promotion Template",
    value:
      "<p>Hi <%- to %>,<br><br>We're currently running an exciting promotion for all our retailer partners. From now until <b>(insert date here)</b>, you'll receive ___% off on orders over $___, when using <b>(insert code here)</b> at checkout. You can also simply click on the promotion banner that's visible on our brand page and the offer will be automatically applied to your cart.<br><br>Happy Shopping!<br><br>Warmly,<br><%- from %></p>",
  },
];

const templateRepOptions: tTemplate[] = [
  {
    label: "Draft Order Template",
    value: `<p>Hi <%- to %>!<br><br>I put together a suggested order for you of some of our best sellers I thought you'd like and do really well in your shop. Of course, you can edit as needed but thought I'd share!<br><br>Here's the order <b>(INSERT LINK TO ORDER HERE)</b>.<br><br>Talk soon,<br><%- from %></p>`,
  },
  {
    label: "New Launches Template",
    value: `<p>Hi <%- to %>!<br><br>We recently launched some new products and wanted to make sure you had seen them. Visit the <a href="<%- brandPageLink %>">brand page</a> and sort by 'Recently Added' to view them.<br><br>I also put them into a draft order for you – feel free to review and consider for your shop!<br><br>Here's the <b>(INSERT LINK TO ORDER HERE)</b> with the new launches.<br><br>Talk soon,<br><%- from %></p>`,
  },
  {
    label: "Checking In Template",
    value: `<p>Hi <%- to %>!<br><br>Hope you're doing well! Wanted to check in and ask how you're doing. How is the brand performing? Do you have any feedback or questions I can help with?<br><br>Warmly,<br><%- from %></p>`,
  },
];

const AddTemplateButton: React.FC = () => {
  const { from = "", to = "" } = useComposer();
  const editor = useSlateStatic();
  const rCompany = useSelector(({ CompanyReducer }) => CompanyReducer);
  const companyIsBrand = rCompany.type === "brand";
  const companyIsRep = rCompany.type === "rep";

  if (!companyIsBrand && !companyIsRep) {
    return null;
  }

  const templates = companyIsBrand ? templateBrandOptions : templateRepOptions;

  const handleOnPress = async (value: string) => {
    const compiled = lodashTemplate(value);
    const data = { from, to, brandPageLink: "" };
    if (rCompany.type === "brand") data.brandPageLink = `${kBaseSiteUrl}/shop_brand?brand=${rCompany.name}`;
    if (rCompany.type === "rep") {
      const connectionsQuery = await connectionDb.queryBrandRepConnection({ repId: rCompany.id }).get();
      const repConnection = connectionsQuery.docs[0]?.data();
      if (repConnection) data.brandPageLink = `${kBaseSiteUrl}/shop_brand?brand=${repConnection.business.name}`;
    }
    insertDataHtml(editor, compiled(data), true);
  };

  return (
    <PdPopover direction="top">
      <PdPopoverTrigger>
        <ComposerToolbarButton leftIcon={<PdPaper style={styles.composerIconSend} />}>
          <ComposerToolbarButtonLabel>Templates</ComposerToolbarButtonLabel>
        </ComposerToolbarButton>
      </PdPopoverTrigger>
      <PdPopoverContent style={styles.addTemplateContent}>
        {templates.map((option, index) => (
          <TouchableOpacity key={option.value} style={styles.p2} onPress={() => handleOnPress(option.value)}>
            <PdText style={styles.textSm}>{`${index + 1}. ${option.label}`}</PdText>
          </TouchableOpacity>
        ))}
      </PdPopoverContent>
    </PdPopover>
  );
};

export default AddTemplateButton;

const styles = StyleSheet.create({
  addTemplateContent: {
    backgroundColor: kColors.lightBrand,
    padding: kStyleConst.kSpacing,
    width: 340,
  },
  composerIconSend: {
    color: kColors.gray500,
    width: 15,
  },
  p2: {
    padding: kStyleConst.kSpacing * 2,
  },
  textSm: {
    fontSize: 12,
  },
});
